import React, { useState } from "react";
import { Star, ShieldCheck, Truck, BadgeCheck, ChevronDown, FlaskConical, Leaf } from "lucide-react";
import { BRAND_TRUST, SAMPLE_REVIEWS } from "../mock";

const nf = (n) => n.toLocaleString("en-GB");

// ---------- Trust badges row ----------
export const TrustBadges = ({ product }) => {
  const reviews = product.reviews || 0;
  const rating = product.rating || BRAND_TRUST.rating;
  return (
    <div className="mt-5 grid grid-cols-3 gap-2 rounded-2xl border border-[#e6f0ea] bg-[#f7fbf8] p-3 text-center">
      <div className="px-1">
        <div className="flex items-center justify-center gap-1 text-[#2D6A4F]">
          <Star size={15} className="fill-current" />
          <span className="font-display text-lg font-bold text-[#1B4332]">{rating}</span>
        </div>
        <p className="mt-0.5 text-[11px] leading-tight text-[#6b7d72]">{nf(reviews)} reviews</p>
      </div>
      <div className="border-x border-[#e6f0ea] px-1">
        <p className="font-display text-lg font-bold text-[#1B4332]">{nf(BRAND_TRUST.reviews)}+</p>
        <p className="mt-0.5 text-[11px] leading-tight text-[#6b7d72]">Trusted store reviews</p>
      </div>
      <div className="px-1">
        <p className="font-display text-lg font-bold text-[#1B4332]">{nf(BRAND_TRUST.shipped)}+</p>
        <p className="mt-0.5 text-[11px] leading-tight text-[#6b7d72]">Orders shipped</p>
      </div>
    </div>
  );
};

// ---------- Important information ----------
export const ImportantInfo = ({ product }) => {
  const [open, setOpen] = useState(true);
  const info = product.importantInfo && product.importantInfo.length
    ? product.importantInfo
    : [
        { title: "Research \u2192 Decide \u2192 Try \u2192 Stick with what works", body: product.description },
        { title: "Clean Product", body: "No nasty extras. No fillers, binders or bulkers. Now improved with our uptake blend \u2014 one of the strongest available, UK manufactured." },
        { title: "Vegan & Allergens", body: product.vegan === false ? "Not vegan. Gluten free, chemical-free BPA-free packaging, 100% active ingredient \u2014 that's it." : "Suitable for vegetarians/vegans where stated. Gluten free, chemical-free BPA-free packaging, 100% active ingredient." },
        { title: "Check the small print", body: "Please check the ingredients list on any supplement you buy. 99.9% use binders, fillers and anti-caking agents \u2014 we don't use any of these." },
      ];

  return (
    <div className="mt-8 overflow-hidden rounded-2xl border border-[#e6f0ea] bg-white">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-3 bg-[#1B4332] px-5 py-4 text-left">
        <span className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white"><FlaskConical size={18} className="text-[#95D5B2]" /> Important Information</span>
        <ChevronDown size={20} className={`text-[#95D5B2] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="divide-y divide-[#eef5f0]">
          {info.map((s, i) => (
            <div key={i} className="px-5 py-4">
              <p className="mb-1 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-[#1B4332]"><Leaf size={14} className="text-[#2D6A4F]" /> {s.title}</p>
              <p className="text-[14px] leading-relaxed text-[#4a5c51]">{s.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Reviews with 5-star-down breakdown graph ----------
const PROPORTIONS = [0.941, 0.024, 0.010, 0.0035, 0.0215]; // 5,4,3,2,1 (mirrors real distribution)

export const ProductReviews = ({ product }) => {
  const total = product.reviews || 0;
  const rating = product.rating || BRAND_TRUST.rating;
  const breakdown = product.reviewBreakdown && product.reviewBreakdown.length === 5
    ? product.reviewBreakdown
    : PROPORTIONS.map((p, i) => (i === 0 ? total - PROPORTIONS.slice(1).reduce((s, x) => s + Math.round(x * total), 0) : Math.round(p * total)));
  const maxCount = Math.max(...breakdown, 1);

  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
      <h2 className="mb-6 font-display text-3xl font-bold uppercase text-[#1B2a21]">Customer Reviews</h2>
      <div className="grid gap-8 lg:grid-cols-3">
        {/* Summary + graph */}
        <div className="rounded-2xl border border-[#e6f0ea] bg-white p-6">
          <div className="text-center">
            <p className="font-display text-5xl font-bold text-[#1B4332]">{rating}</p>
            <div className="mt-1 flex justify-center text-[#2D6A4F]">
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={18} className="fill-current" />)}
            </div>
            <p className="mt-1 text-sm text-[#6b7d72]">Based on {nf(total)} reviews</p>
          </div>
          <div className="mt-5 space-y-2">
            {[5, 4, 3, 2, 1].map((stars, idx) => (
              <div key={stars} className="flex items-center gap-2 text-sm">
                <span className="flex w-10 items-center gap-1 text-[#34463c]">{stars}<Star size={12} className="fill-current text-[#2D6A4F]" /></span>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#f0f7f2]">
                  <div className="h-full rounded-full bg-[#2D6A4F]" style={{ width: `${(breakdown[idx] / maxCount) * 100}%` }} />
                </div>
                <span className="w-12 text-right text-[#8aa093]">{nf(breakdown[idx])}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Review list */}
        <div className="space-y-4 lg:col-span-2">
          {SAMPLE_REVIEWS.map((r) => (
            <div key={r.name + r.title} className="rounded-2xl border border-[#e6f0ea] bg-white p-5">
              <div className="mb-1 flex items-center justify-between">
                <div className="flex text-[#2D6A4F]">{[0, 1, 2, 3, 4].map((s) => <Star key={s} size={14} className="fill-current" />)}</div>
                <span className="text-xs text-[#8aa093]">Verified buyer</span>
              </div>
              <p className="font-display text-sm font-bold uppercase text-[#1B2a21]">{r.title}</p>
              <p className="mt-1 text-[14px] leading-relaxed text-[#4a5c51]">{r.body}</p>
              <p className="mt-2 text-xs font-semibold text-[#2D6A4F]">{r.name}</p>
            </div>
          ))}
          <p className="text-center text-xs text-[#8aa093]">Sample reviews shown — your live Judge.me reviews appear here once connected.</p>
        </div>
      </div>
    </section>
  );
};
